import React, { useState } from 'react';
import { Phone, MapPin, Calendar, ChevronDown, ChevronUp, Package, User, Check, Trash2, Clock, Edit, MessageSquare, FileText, CheckCircle2, Eye, ArrowRight, RefreshCw, Truck, Receipt } from 'lucide-react';
import { getStatusColor, getStatusLabel, getStatusAccent } from '../../../../config/orderStatus';
import { formatDateTime } from '../../../../utils/dateUtils';

const formatDate = (date, timeSlot) => {
    if (!date) return timeSlot || '';
    const dateStr = formatDateTime(date, { hour: undefined, minute: undefined });
    return timeSlot ? `${dateStr} · ${timeSlot}` : dateStr;
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 2
    }).format(amount || 0);
};

const SalesOrderCard = ({
    order,
    onTransition,
    onRecordSale,
    transitions = [],
    loadingTransition,
    isSelected,
    onSelect,
    isSelectionAllowed = true,
    onViewDetail,
    canEdit,
    onEdit,
    deliverySequence,
}) => {
    const [showItems, setShowItems] = useState(false);
    const accent = getStatusAccent(order.status);

    // Generate a consistent avatar color based on name
    const getAvatarColor = (name) => {
        const colors = [
            'from-amber-500 to-orange-600',
            'from-blue-500 to-indigo-600',
            'from-emerald-500 to-teal-600',
            'from-purple-500 to-indigo-600',
            'from-rose-500 to-pink-600'
        ];
        let hash = 0;
        for (let i = 0; i < (name || '').length; i++) {
            hash = (name || '').charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    const itemsList = order.items || [];
    const totalItemsCount = itemsList.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const subtotalExVat = itemsList.reduce((sum, item) => sum + (item.total || ((item.quantity || 0) * (item.unitPrice || 0)) || 0), 0);
    const totalAmount = order.totalAmount || subtotalExVat;
    const totalVat = Math.max(0, totalAmount - subtotalExVat);
    const visibleItems = showItems ? itemsList : itemsList.slice(0, 3);
    const hiddenItemsCount = Math.max(0, itemsList.length - 3);

    return (
        <div
            className={`group relative bg-white rounded-3xl border transition-all duration-300 overflow-hidden flex flex-col ${isSelected
                ? 'border-blue-500 ring-2 ring-blue-500/10 shadow-lg'
                : 'border-gray-100 hover:border-blue-200 shadow-sm hover:shadow-md'
                } ${onViewDetail ? 'cursor-pointer' : ''}`}
            onClick={onViewDetail ? () => onViewDetail(order) : undefined}
            role={onViewDetail ? 'button' : undefined}
            tabIndex={onViewDetail ? 0 : undefined}
            onKeyDown={onViewDetail ? (e) => { if (e.key === 'Enter') onViewDetail(order); } : undefined}
        >
            <div className="flex-1 p-4 md:p-5 flex flex-col gap-3.5">
                {/* ── Top Header: Avatar + Customer Name + Status Badge & Checkbox ── */}
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                        {/* Customer Initials Avatar */}
                        <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${getAvatarColor(order.customer?.name)} flex items-center justify-center text-white font-black text-lg shadow-md shadow-orange-100/50 shrink-0`}>
                            {(order.customer?.name || 'U')[0]}
                        </div>
                        <div className="min-w-0 flex-1 pt-0.5">
                            <div className="flex items-center gap-2">
                                {deliverySequence != null && (
                                    <span className="shrink-0 w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[9px] font-black shadow-sm">
                                        {deliverySequence}
                                    </span>
                                )}
                                <h3 className="font-black text-gray-900 text-base leading-snug tracking-tight break-words">
                                    {order.customer?.name || 'Unassigned Customer'}
                                </h3>
                            </div>
                            {/* Order Ref and Salesperson (Single line, fully displayed without trim or wrap!) */}
                            <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-gray-500 font-semibold whitespace-nowrap">
                                <span className="text-gray-700 font-bold shrink-0">
                                    {order.orderRef || `#${(order.id || '').slice(-8)}`}
                                </span>
                                {(order.orderSalesman || order.customerSalesman || order.salesman) && (
                                    <span className="inline-flex items-center gap-1 shrink-0">
                                        <span className="text-gray-300">·</span>
                                        <User size={11} className="text-gray-400 shrink-0" />
                                        <span>{order.orderSalesman || order.customerSalesman || order.salesman}</span>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Status Badge & Checkbox */}
                    <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-2.5 py-1 rounded-full text-[9px] sm:text-[10px] uppercase tracking-wider font-extrabold ${getStatusColor(order.status)} shrink-0 text-center shadow-xs`}>
                            {getStatusLabel(order.status)}
                        </span>
                        {isSelectionAllowed && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect(order.id);
                                }}
                                className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-200 shrink-0 ${isSelected
                                    ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                                    : 'bg-white border-gray-200 text-transparent hover:border-blue-300'
                                    }`}
                            >
                                <Check size={13} strokeWidth={3} />
                            </button>
                        )}
                    </div>
                </div>

                {/* ── Compact Metadata Horizontal Pill Bar (Phone, Address, Delivery Date) ── */}
                <div className="flex flex-wrap items-center gap-2">
                    {order.customer?.phone && (
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-100/70 hover:bg-gray-100 text-[11px] font-semibold text-gray-700 transition-colors">
                            <Phone size={12} className="text-gray-400 shrink-0" />
                            <span>{order.customer.phone}</span>
                        </div>
                    )}
                    {order.customer?.address && (
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-100/70 hover:bg-gray-100 text-[11px] font-semibold text-gray-700 transition-colors max-w-[220px] truncate" title={order.customer.address}>
                            <MapPin size={12} className="text-gray-400 shrink-0" />
                            <span className="truncate">{order.customer.address}</span>
                        </div>
                    )}
                    {(order.deliveryDate || order.timeSlot) && (() => {
                        const now = Date.now();
                        const deliveryMs = order.deliveryDate ? new Date(order.deliveryDate).getTime() : null;
                        const hoursUntil = deliveryMs != null ? (deliveryMs - now) / 3_600_000 : null;
                        const isUrgent = hoursUntil != null && hoursUntil < 3;
                        return (
                            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-colors ${
                                isUrgent ? 'bg-red-50 text-red-600 border-red-100' : 'bg-rose-50/70 text-rose-600 border-rose-100'
                            }`}>
                                <Calendar size={12} className={isUrgent ? 'text-red-500 shrink-0' : 'text-rose-500 shrink-0'} />
                                <span>{formatDate(order.deliveryDate, order.timeSlot)}</span>
                            </div>
                        );
                    })()}
                </div>

                {order.notes && (
                    <div className="flex items-start gap-2 text-xs text-purple-700 bg-purple-50/60 border border-purple-100 rounded-xl p-2.5">
                        <MessageSquare size={13} className="text-purple-500 shrink-0 mt-0.5" />
                        <span className="font-medium leading-relaxed line-clamp-2" title={order.notes}>{order.notes}</span>
                    </div>
                )}

                {/* ── Order Items Container ── */}
                <div className="bg-gray-50/80 rounded-2xl p-3 md:p-3.5 border border-gray-100/80">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(37,99,235,0.4)]" />
                            <span className="text-xs font-black text-gray-900 uppercase tracking-wider">Order Items</span>
                        </div>
                        <span className="text-[10px] font-black text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-100/60">
                            {totalItemsCount} {totalItemsCount === 1 ? 'ITEM' : 'ITEMS'}
                        </span>
                    </div>

                    {/* Product Rows with Image/Thumbnail Icon Support */}
                    <div className="space-y-2">
                        {visibleItems.map((item, idx) => {
                            const itemImage = item.image || item.photo || item.product?.image || item.imageUrl;
                            return (
                                <div key={idx} className="flex items-center justify-between gap-3 p-1.5 rounded-xl hover:bg-white transition-colors">
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        {/* Product Photo / Icon Thumbnail */}
                                        {itemImage ? (
                                            <img
                                                src={itemImage}
                                                alt={item.name || 'Product'}
                                                className="w-10 h-10 rounded-xl object-cover border border-gray-100 shadow-xs shrink-0"
                                            />
                                        ) : (
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100/80 flex items-center justify-center text-indigo-500 shrink-0 shadow-xs">
                                                <Package size={18} />
                                            </div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs font-bold text-gray-900 leading-tight break-words" title={item.name}>
                                                {item.name || item.productName || 'Unknown Product'}
                                            </p>
                                            <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md inline-block mt-0.5 uppercase tracking-wider">
                                                {item.quantity} {item.unit || 'PCS'}
                                            </span>
                                        </div>
                                    </div>
                                    <span className="text-xs font-black text-gray-900 shrink-0">
                                        {formatCurrency(item.total || item.totalPrice || ((item.quantity || 0) * (item.unitPrice || 0)))}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {itemsList.length > 3 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowItems(!showItems); }}
                            className="w-full mt-2.5 flex items-center justify-center gap-1.5 py-1.5 text-[10px] font-black uppercase tracking-wider text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                        >
                            {showItems ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            {showItems ? 'Show Less' : `Show ${hiddenItemsCount} More Items`}
                        </button>
                    )}
                </div>

                {/* ── Total Amount Summary Line ── */}
                <div className="flex items-center justify-between px-1 py-1 border-t border-dashed border-gray-100 mt-1">
                    <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Amount (Incl. VAT)</span>
                        <span className="text-[9px] font-bold text-gray-400">
                            Excl. VAT: {formatCurrency(subtotalExVat)} | VAT: {formatCurrency(totalVat)}
                        </span>
                    </div>
                    <span className="text-xl font-black text-blue-600 drop-shadow-xs">{formatCurrency(totalAmount)}</span>
                </div>
            </div>

            {/* ── Action Buttons Footer (Balanced Flex Layout for 2-3 Action Buttons) ── */}
            <div className="p-4 pt-0 flex items-center gap-2 flex-wrap w-full">
                {/* View Details button */}
                {onViewDetail && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onViewDetail(order); }}
                        className="flex-1 min-w-[100px] py-2.5 px-3 rounded-xl border border-blue-200 text-blue-600 bg-white hover:bg-blue-50 font-bold text-xs flex items-center justify-center gap-1.5 transition-all shadow-xs"
                    >
                        <Eye size={14} className="text-blue-500 shrink-0" />
                        <span>View Details</span>
                    </button>
                )}

                {canEdit && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onEdit(order); }}
                        disabled={!!loadingTransition}
                        className="py-2.5 px-3 rounded-xl text-xs font-bold bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-100 flex items-center justify-center gap-1.5 transition-all disabled:opacity-50"
                    >
                        <Edit size={14} className="shrink-0" />
                        <span>Edit</span>
                    </button>
                )}

                {transitions.reduce((uniq, t) => {
                    const getCanonicalId = (item) => {
                        const raw = (item.transitionName || item.label || item.name || item.action || '').toLowerCase();
                        if (raw.includes('deliver')) return 'deliver';
                        if (raw.includes('warehouse')) return 'warehouse';
                        if (raw.includes('cancel')) return 'cancel';
                        if (raw.includes('reject')) return 'reject';
                        if (raw.includes('return')) return 'return';
                        if (raw.includes('approve')) return 'approve';
                        if (raw.includes('ready')) return 'ready';
                        return raw.replace(/\bas\b/g, '').trim();
                    };
                    if (!uniq.some(ext => getCanonicalId(ext) === getCanonicalId(t))) {
                        uniq.push(t);
                    }
                    return uniq;
                }, []).map((t) => {
                    const style = getTransitionStyle(t);
                    const rawLabel = t.transitionName || t.label || t.name || t.action || '';
                    let cleanLabel = rawLabel;
                    if (rawLabel.toLowerCase().includes('send to warehouse')) cleanLabel = 'To Warehouse';
                    if (rawLabel.toLowerCase().includes('deliver')) cleanLabel = 'Deliver';
                    
                    const isResubmit = rawLabel.toLowerCase().includes('accept revision') || rawLabel.toLowerCase().includes('confirm revision') || rawLabel.toLowerCase().includes('resubmit');
                    if (isResubmit) cleanLabel = 'Resubmit';

                    const isModificationRequired = order.status === 'REQUEST_FOR_MODIFICATION';
                    const hasAnyRevision = itemsList.some(i => i.warehouseQuantity !== null && i.warehouseQuantity !== undefined && Number(i.warehouseQuantity) !== Number(i.quantity));

                    if (isResubmit && isModificationRequired && hasAnyRevision && !canEdit) {
                        return null;
                    }

                    const isDisabled = !!loadingTransition || (isResubmit && isModificationRequired && hasAnyRevision);
                    const isDeliver = cleanLabel === 'Deliver' || rawLabel.toLowerCase().includes('deliver');

                    return (
                        <React.Fragment key={t.publicId || t.id}>
                            <button
                                onClick={(e) => { e.stopPropagation(); onTransition(order, t); }}
                                disabled={isDisabled}
                                title={isDisabled && isResubmit ? "Address modifications by editing the order first" : cleanLabel}
                                className={`flex-1 min-w-[100px] py-2.5 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all shadow-xs disabled:opacity-50 ${style}`}
                            >
                                {loadingTransition ? (
                                    <RefreshCw size={14} className="animate-spin shrink-0" />
                                ) : (
                                    <>
                                        {getTransitionIcon(t)}
                                        <span>{cleanLabel}</span>
                                    </>
                                )}
                            </button>
                            {isDeliver && onRecordSale && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onRecordSale(order); }}
                                    disabled={isDisabled}
                                    className={`flex-1 min-w-[100px] py-2.5 px-3 rounded-xl text-xs font-bold border shadow-xs transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 ${order.recordedSale ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100' : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'}`}
                                >
                                    {order.recordedSale ? <CheckCircle2 size={14} className="text-emerald-600 shrink-0" /> : <Receipt size={14} className="text-blue-600 shrink-0" />}
                                    <span>{order.recordedSale ? 'Recorded' : 'Record Sale'}</span>
                                </button>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

const getTransitionIcon = (transition) => {
    const name = (transition.transitionName || transition.label || transition.name || transition.action || '').toLowerCase();
    if (name.includes('cancel')) return <Trash2 size={14} className="shrink-0" />;
    if (name.includes('reject') || name.includes('return')) return <RefreshCw size={14} className="shrink-0" />;
    if (name.includes('accept revision') || name.includes('confirm revision') || name.includes('resubmit')) return <Check size={14} className="shrink-0" />;
    if (name.includes('warehouse') || name.includes('send')) return <Package size={14} className="shrink-0" />;
    if (name.includes('approve')) return <Check size={14} className="shrink-0" />;
    if (name.includes('dispatch') || name.includes('deliver')) return <Truck size={14} className="shrink-0" />;
    return <ArrowRight size={14} className="shrink-0" />;
};

const getTransitionStyle = (transition) => {
    const name = (transition.transitionName || transition.label || transition.name || transition.action || '').toLowerCase();
    if (name.includes('cancel') || name.includes('reject')) {
        return 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-100';
    }
    if (name.includes('accept revision') || name.includes('confirm revision') || name.includes('resubmit')) {
        return 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-md shadow-emerald-200';
    }
    if (name.includes('revision') || name.includes('revise') || name.includes('edit')) {
        return 'bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-100';
    }
    if (name.includes('return')) {
        return 'bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-100';
    }
    if (name.includes('warehouse') || name.includes('send') || name.includes('approve') || name.includes('resubmit')) {
        return 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-md shadow-emerald-200';
    }
    if (name.includes('dispatch') || name.includes('despatch') || name.includes('delivery')) {
        return 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-200';
    }
    if (name.includes('deliver')) {
        return 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md shadow-emerald-200';
    }
    return 'bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-200';
};

export default SalesOrderCard;
